import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { colors } from '../../styles/theme';
import {
  CAD_SORT_OPTIONS,
  CAD_STOCK_LABELS,
  CAD_STOCK_STATUSES,
} from '../data/cadConstants';
import { normalizeCadStatusFilter } from '../utils/cadParcelDisplay';
import {
  showroomCompactMenuButtonSx,
  showroomCountsRowSx,
  showroomMenuItemSx,
  showroomMenuPaperSx,
} from '../Showroom.styles';
import { CAD_PARCEL_STOCK_STYLE } from '../../Map/styles/mapTheme';
import {
  listSearchFieldSx,
  listSearchIconSx,
} from '../../Map/components/UserLotListsPanel/UserLotListsPanel.styles';

const STATUS_CHECKBOX_SX = {
  p: 0,
  color: colors.textSecondary,
  '&.Mui-checked': { color: colors.primary },
};

function CompactMenuButton({ label, value, onClick, 'aria-label': ariaLabel }) {
  return (
    <Button
      size="small"
      onClick={onClick}
      endIcon={<ExpandMoreIcon sx={{ fontSize: '16px !important' }} />}
      aria-label={ariaLabel}
      sx={showroomCompactMenuButtonSx}
    >
      {label}
      {' '}
      <Box
        component="span"
        sx={{ color: colors.textPanelTitle, fontWeight: 500, ml: 0.5 }}
      >
        {value}
      </Box>
    </Button>
  );
}

/**
 * Estado es multi-selección: el buscador puede devolver más de uno
 * (“disponibles o en oferta”) y el chip tiene que poder representarlo.
 */
export default function ShowroomManageFilters({
  searchQuery = '',
  onSearchChange,
  statusFilter = null,
  onStatusChange,
  featuresCount,
  counts,
  sortBy,
  onSortChange,
  showStatusFilter = true,
  sortOptions = CAD_SORT_OPTIONS,
}) {
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
  const options = Array.isArray(sortOptions) && sortOptions.length ? sortOptions : CAD_SORT_OPTIONS;
  const sortLabel = options.find((option) => option.value === sortBy)?.label || 'Nombre';
  const selected = normalizeCadStatusFilter(statusFilter);
  const countOf = (status) => counts?.[status] || 0;
  const statusLabel = (() => {
    if (!selected) return `Todos · ${featuresCount}`;
    if (selected.length === 1) {
      return `${CAD_STOCK_LABELS[selected[0]] || selected[0]} · ${countOf(selected[0])}`;
    }
    const total = selected.reduce((acc, status) => acc + countOf(status), 0);
    return `${selected.length} estados · ${total}`;
  })();

  const toggleStatus = (status) => {
    const current = selected || [];
    const next = current.includes(status)
      ? current.filter((item) => item !== status)
      : [...current, status];
    onStatusChange?.(next.length ? next : null);
  };

  return (
    <>
      {onSearchChange ? (
        <TextField
          size="small"
          fullWidth
          placeholder="Buscar por nombre o descripción"
          value={searchQuery}
          onChange={(event) => onSearchChange(event.target.value)}
          sx={{ ...listSearchFieldSx, mt: 0 }}
          inputProps={{ maxLength: 80 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={listSearchIconSx} />
              </InputAdornment>
            ),
          }}
        />
      ) : null}

      <Box sx={showroomCountsRowSx}>
        {showStatusFilter ? (
          <>
            <CompactMenuButton
              label="Estado:"
              value={statusLabel}
              aria-label="Filtrar por estado"
              onClick={(event) => setStatusMenuAnchor(event.currentTarget)}
            />
            <Menu
              anchorEl={statusMenuAnchor}
              open={Boolean(statusMenuAnchor)}
              onClose={() => setStatusMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{ sx: showroomMenuPaperSx }}
            >
              <MenuItem
                selected={!selected}
                onClick={() => onStatusChange?.(null)}
                sx={showroomMenuItemSx}
              >
                Todos · {featuresCount}
              </MenuItem>
              {CAD_STOCK_STATUSES.map((status) => (
                <MenuItem
                  key={status}
                  selected={Boolean(selected?.includes(status))}
                  onClick={() => toggleStatus(status)}
                  sx={showroomMenuItemSx}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Checkbox
                      size="small"
                      checked={Boolean(selected?.includes(status))}
                      tabIndex={-1}
                      disableRipple
                      sx={STATUS_CHECKBOX_SX}
                    />
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: CAD_PARCEL_STOCK_STYLE.stockFill[status],
                        flexShrink: 0,
                      }}
                    />
                    {CAD_STOCK_LABELS[status]} · {countOf(status)}
                  </Box>
                </MenuItem>
              ))}
            </Menu>
          </>
        ) : <Box />}
        <CompactMenuButton
          label="Ordenar por:"
          value={sortLabel}
          aria-label="Ordenar lotes"
          onClick={(event) => setSortMenuAnchor(event.currentTarget)}
        />
        <Menu
          anchorEl={sortMenuAnchor}
          open={Boolean(sortMenuAnchor)}
          onClose={() => setSortMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: showroomMenuPaperSx }}
        >
          {options.map((option) => (
            <MenuItem
              key={option.value}
              selected={sortBy === option.value}
              onClick={() => {
                onSortChange(option.value);
                setSortMenuAnchor(null);
              }}
              sx={showroomMenuItemSx}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      </Box>
    </>
  );
}
